import { useCallback, useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import {
  adminApi,
  type AdminCoachTrainingListResult,
  type AdminCoachTrainingStage,
} from "api/adminApi";
import {
  Badge,
  Button,
  ErrorBanner,
  Field,
  Input,
  Modal,
  NUMERIC,
  SectionHeader,
  TD,
  TEmpty,
  TH,
  THead,
  TRow,
  TSkeleton,
  Table,
} from "pages/admin/ui";
import { isCoachEmail } from "./coachFormat";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";
const COLUMN_COUNT = 4;

const STAGE: Record<
  AdminCoachTrainingStage,
  { label: string; tone: "warn" | "info" }
> = {
  signup_pending: { label: "가입 대기", tone: "warn" },
  account_ready: { label: "계정 세팅 완료", tone: "info" },
};

/**
 * 코치 관리: 교육. 실무 투입 전 코치를 추가하고 진행 단계를 본다.
 * 추가하면 가입 대기 → 그 이메일로 구글 가입하면 서버가 코치 계정·센드버드 유저를 세팅한다 (계정 세팅 완료).
 */
export default function CoachTrainingSection() {
  const [data, setData] = useState<AdminCoachTrainingListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // 추가 직후 다시 받는 요청과 겹쳐도 마지막 응답만 반영한다
  const requestSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.coachTrainingList();
      if (seq !== requestSeq.current) return;
      if (res.code === 0 && res.result) setData(res.result);
      else setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      const code = (e as AxiosError).response?.status;
      setError(
        code === 403 ? "권한이 없는 계정이에요" : FALLBACK_ERROR_MESSAGE
      );
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const coaches = data?.coaches ?? [];

  return (
    <section className="flex flex-col gap-4">
      <SectionHeader title="교육 코치" count={`${coaches.length}명`}>
        <Button variant="primary" onClick={() => setCreating(true)}>
          신규 코치 추가
        </Button>
      </SectionHeader>

      <p className="text-sm text-slate-500">
        실무 투입 전 코치예요. 추가한 이메일로 구글 가입하면 코치 계정이
        세팅돼요.
      </p>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <Table>
        <THead>
          <TH>코치</TH>
          <TH>이메일</TH>
          <TH numeric>회원번호</TH>
          <TH help="가입 대기: 추가만 하고 아직 가입 전 → 계정 세팅 완료: 그 이메일로 구글 가입해 코치 계정·채팅 계정이 만들어졌어요.">
            단계
          </TH>
        </THead>
        <tbody>
          {loading && !data ? (
            <TSkeleton colSpan={COLUMN_COUNT} />
          ) : coaches.length === 0 ? (
            <TEmpty colSpan={COLUMN_COUNT}>교육 중인 코치가 없어요</TEmpty>
          ) : (
            coaches.map((row) => (
              <TRow key={row.email}>
                <TD>
                  <span className="font-medium text-slate-900">{row.name}</span>
                </TD>
                <TD>
                  <span className="text-slate-600">{row.email}</span>
                </TD>
                <TD numeric>
                  {row.coachId ?? <span className="text-slate-300">-</span>}
                </TD>
                <TD>
                  <Badge tone={STAGE[row.stage].tone}>
                    {STAGE[row.stage].label}
                  </Badge>
                </TD>
              </TRow>
            ))
          )}
        </tbody>
      </Table>

      {creating && (
        <CoachCreateModal
          onCreated={() => {
            setCreating(false);
            load();
          }}
          onClose={() => setCreating(false)}
        />
      )}
    </section>
  );
}

function CoachCreateModal({
  onCreated,
  onClose,
}: {
  onCreated: () => void;
  onClose: () => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 칸을 비운 채로는 오류를 띄우지 않는다. 형식이 틀린 값을 넣었을 때만 알린다
  const emailError =
    email.trim() !== "" && !isCoachEmail(email)
      ? "coach{숫자}@orwellhealth.org 형식으로 입력해 주세요"
      : null;
  const canSubmit = isCoachEmail(email) && name.trim() !== "";

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await adminApi.coachTrainingCreate(
        email.trim().toLowerCase(),
        name.trim()
      );
      if (res.code === 0) onCreated();
      else setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="신규 코치 추가"
      description="가입 대기로 추가돼요. 이 이메일로 구글 가입하면 코치 계정이 세팅돼요."
      onClose={onClose}
      footer={
        <Button
          variant="primary"
          disabled={!canSubmit}
          loading={saving}
          loadingLabel="추가 중"
          onClick={submit}
        >
          추가
        </Button>
      }
    >
      <form
        className="mt-4 flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label="코치 이메일" error={emailError}>
          <Input
            className={NUMERIC}
            placeholder="coach12@orwellhealth.org"
            value={email}
            invalid={emailError != null}
            autoFocus
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="코치 이름" helper="가입하면 닉네임이 돼요">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        {/* Enter 로 제출 */}
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
